import { useMutation, useQuery } from '@tanstack/react-query';
import { Badge, Button, Icon, ListGroup, ListRow, Option, OptionList, SegmentedControl } from '@uplink/ui';
import qrcode from 'qrcode-generator';
import { useMemo, useState } from 'react';
import { errorText, useTRPC } from './api.ts';
import { usePrefs } from './prefs.tsx';
import { ConfirmButton, CopyText } from './servers-ui.tsx';
import { haptic, openPayUrl } from './telegram.ts';

export type VpnKind = 'vless' | 'amneziawg' | 'outline' | 'wireguard';
export type ClientPlatform = 'apple' | 'android' | 'windows' | 'linux';
type VlessAppId = 'v2box' | 'v2raytun' | 'happ' | 'hiddify' | 'v2rayn' | 'v2raya';
export type VpnApp = {
  id?: VlessAppId;
  name: string;
  platforms: string;
  url: string;
  clientPlatforms?: ClientPlatform[];
};

/** Определяет устройство для первичного выбора, но пользователь всегда может переключить платформу вручную. */
export function guessClientPlatform(): ClientPlatform {
  const ua = navigator.userAgent;
  if (/android/i.test(ua)) return 'android';
  if (/iphone|ipad|ipod/i.test(ua)) return 'apple';
  if (/windows/i.test(ua)) return 'windows';
  if (/mac/i.test(ua)) return 'apple';
  return 'linux';
}

/** The VPNs Uplink installs, best for Russia first */
export const VPN_OPTIONS: { kind: VpnKind; name: string; recommended?: boolean }[] = [
  { kind: 'vless', name: 'VLESS Reality', recommended: true },
  { kind: 'amneziawg', name: 'AmneziaWG' },
  { kind: 'outline', name: 'Outline' },
  { kind: 'wireguard', name: 'WireGuard' },
];

/** Client apps to connect with, per VPN */
const APPS: Record<VpnKind, VpnApp[]> = {
  // The key uses the XHTTP transport: only clients on a recent Xray or sing-box core read it
  vless: [
    {
      id: 'v2box',
      name: 'V2Box',
      platforms: 'iPhone, iPad, macOS',
      url: 'https://apps.apple.com/app/id6446814690',
      clientPlatforms: ['apple'],
    },
    {
      id: 'v2box',
      name: 'V2Box',
      platforms: 'Android · Google Play',
      url: 'https://play.google.com/store/apps/details?id=dev.hexasoftware.v2box',
      clientPlatforms: ['android'],
    },
    {
      id: 'v2raytun',
      name: 'v2RayTun',
      platforms: 'Android · Google Play',
      url: 'https://play.google.com/store/apps/details?id=com.v2raytun.android',
      clientPlatforms: ['android'],
    },
    {
      id: 'happ',
      name: 'Happ',
      platforms: 'iOS, Android, Windows, macOS',
      url: 'https://www.happ.su/main/ru',
      clientPlatforms: ['apple', 'android', 'windows'],
    },
    {
      id: 'hiddify',
      name: 'Hiddify 4+',
      platforms: 'iOS, Android, Windows, macOS, Linux',
      url: 'https://hiddify.com/app/',
      clientPlatforms: ['apple', 'android', 'windows', 'linux'],
    },
    {
      id: 'v2rayn',
      name: 'v2rayN',
      platforms: 'Windows 10/11 · ZIP',
      url: 'https://github.com/2dust/v2rayN/releases/latest/download/v2rayN-windows-64.zip',
      clientPlatforms: ['windows'],
    },
    {
      id: 'v2raya',
      name: 'v2rayA',
      platforms: 'Linux · Snap Store',
      url: 'https://snapcraft.io/v2raya',
      clientPlatforms: ['linux'],
    },
  ],
  amneziawg: [
    {
      name: 'AmneziaVPN',
      platforms: 'iOS, Android, Windows, macOS, Linux',
      url: 'https://amnezia.org/downloads',
    },
  ],
  outline: [
    {
      name: 'Outline',
      platforms: 'iOS, Android, Windows, macOS, Linux',
      url: 'https://getoutline.org/get-started/#step-3',
    },
  ],
  wireguard: [
    {
      name: 'WireGuard',
      platforms: 'iOS, Android, Windows, macOS, Linux',
      url: 'https://www.wireguard.com/install/',
    },
  ],
};

/** Steps the installer reports, grouped into what the user sees */
const PROGRESS: Record<string, number> = {
  packages: 1,
  'kernel module': 2,
  docker: 2,
  panel: 2,
  server: 2,
  config: 3,
  key: 3,
};

/** A QR code drawn as one SVG path: dark modules on a white tile, readable in either theme. */
export function QrCode({ text, label }: { text: string; label?: string }) {
  const path = useMemo(() => {
    const qr = qrcode(0, 'M');
    qr.addData(text, 'Byte');
    qr.make();
    const n = qr.getModuleCount();
    let d = '';
    for (let r = 0; r < n; r++)
      for (let c = 0; c < n; c++) if (qr.isDark(r, c)) d += `M${c + 4} ${r + 4}h1v1h-1z`;
    return { d, size: n + 8 };
  }, [text]);
  return (
    <svg className="ul-qr" viewBox={`0 0 ${path.size} ${path.size}`} role="img" aria-hidden={!label}>
      <title>{label ?? 'QR'}</title>
      <rect width={path.size} height={path.size} fill="#fff" />
      <path d={path.d} fill="#0d1524" shapeRendering="crispEdges" />
    </svg>
  );
}

/** Единый список приложений для подключения к обычному и пробному VPN. */
export function VpnAppsList({
  kind,
  platform,
  onPlatformChange,
  onSelect,
}: {
  kind: VpnKind;
  platform: ClientPlatform;
  onPlatformChange: (platform: ClientPlatform) => void;
  onSelect: (app: VpnApp) => void;
}) {
  const { t } = usePrefs();
  return (
    <ListGroup header={t.vpn.howTo} footer={t.vpn.howToFooter[kind]}>
      {kind === 'vless' ? (
        <SegmentedControl<ClientPlatform>
          label={t.vpn.clientPlatform}
          value={platform}
          onChange={onPlatformChange}
          options={(Object.keys(t.vpn.clientPlatforms) as ClientPlatform[]).map((item) => ({
            value: item,
            label: t.vpn.clientPlatforms[item],
          }))}
        />
      ) : null}
      {APPS[kind]
        .filter((app) => !app.clientPlatforms || app.clientPlatforms.includes(platform))
        .map((app) => (
          <ListRow
            key={`${app.name}-${app.url}`}
            icon="arrowDown"
            title={app.name}
            subtitle={app.platforms}
            value={t.vpn.install}
            chevron
            onClick={() => onSelect(app)}
          />
        ))}
    </ListGroup>
  );
}

/** Единая пошаговая инструкция выбранного VLESS-клиента. */
export function VpnAppGuide({ app, vpnKey, onBack }: { app: VpnApp; vpnKey: string; onBack: () => void }) {
  const { t } = usePrefs();
  if (!app.id) return null;
  return (
    <div className="ul-stack">
      <button type="button" className="ul-link ul-link--muted" onClick={onBack}>
        ← {t.common.back}
      </button>
      <h2 className="ul-group__header">{t.vpn.appGuideTitle(app.name)}</h2>
      <ListGroup header={t.vpn.appGuideInstall(app.name)} footer={t.vpn.appGuideInstallHint}>
        <ListRow
          icon="arrowDown"
          title={app.name}
          subtitle={app.platforms}
          value={t.vpn.install}
          chevron
          onClick={() => openPayUrl(app.url)}
        />
      </ListGroup>
      <ListGroup header={t.vpn.appGuideCopy} footer={t.vpn.appGuideCopyHint}>
        <ListRow
          icon="key"
          title={<span className="ul-mono ul-wrap ul-clamp">{vpnKey}</span>}
          trailing={<CopyText text={vpnKey} />}
        />
      </ListGroup>
      <div className="ul-group-wrap">
        <div className="ul-group__header">{t.vpn.appGuideImport(app.name)}</div>
        <div className="ul-group">
          <ol className="ul-guide">
            {t.vpn.appGuideSteps[app.id].map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
        </div>
      </div>
      <button type="button" className="ul-link ul-link--muted" onClick={onBack}>
        {t.vpn.backToApps}
      </button>
    </div>
  );
}

/** Pick a VPN and get the command that installs it on this server over SSH. */
function InstallByCommand({ id, retry }: { id: string; retry?: boolean }) {
  const trpc = useTRPC();
  const { t } = usePrefs();
  const [kind, setKind] = useState<VpnKind>('vless');
  const [command, setCommand] = useState<string | null>(null);
  const issue = useMutation(
    trpc.servers.vpnCommand.mutationOptions({
      onSuccess: (r) => {
        haptic.success();
        setCommand(r.command);
      },
      onError: () => haptic.error(),
    }),
  );
  if (command) {
    return (
      <>
        <ol className="ul-guide">
          {t.vpn.commandSteps.map((s) => (
            <li key={s}>{s}</li>
          ))}
        </ol>
        <ListGroup footer={t.vpn.commandNote}>
          <ListRow
            icon="terminal"
            title={<span className="ul-mono ul-wrap">{command}</span>}
            trailing={<CopyText text={command} />}
          />
        </ListGroup>
      </>
    );
  }
  return (
    <>
      <OptionList label={t.vpn.which}>
        {VPN_OPTIONS.map((o) => (
          <Option
            key={o.kind}
            selected={o.kind === kind}
            onSelect={() => {
              haptic.select();
              setKind(o.kind);
            }}
            title={o.name}
            subtitle={t.vpn.about[o.kind]}
            trailing={o.recommended ? <Badge tone="accent">{t.vpn.recommended}</Badge> : undefined}
          />
        ))}
      </OptionList>
      <Button block icon="shield" loading={issue.isPending} onClick={() => issue.mutate({ id, kind })}>
        {retry ? t.vpn.retry : t.vpn.getCommand}
      </Button>
      {issue.isError ? (
        <div className="ul-error" role="alert">
          {errorText(issue.error)}
        </div>
      ) : null}
    </>
  );
}

/** Устанавливает выбранный VPN по SSH с сохранённым паролем; команда остаётся запасным вариантом. */
function InstallAutomatically({ id, onQueued }: { id: string; onQueued: () => Promise<unknown> }) {
  const trpc = useTRPC();
  const { t } = usePrefs();
  const [kind, setKind] = useState<VpnKind>('vless');
  const [manual, setManual] = useState(false);
  const install = useMutation(
    trpc.servers.vpnRetry.mutationOptions({
      onSuccess: async () => {
        haptic.success();
        await onQueued();
      },
      onError: () => haptic.error(),
    }),
  );
  return (
    <>
      <OptionList label={t.vpn.which}>
        {VPN_OPTIONS.map((o) => (
          <Option
            key={o.kind}
            selected={o.kind === kind}
            onSelect={() => {
              haptic.select();
              setKind(o.kind);
            }}
            title={o.name}
            subtitle={t.vpn.about[o.kind]}
            trailing={o.recommended ? <Badge tone="accent">{t.vpn.recommended}</Badge> : undefined}
          />
        ))}
      </OptionList>
      <Button block icon="shield" loading={install.isPending} onClick={() => install.mutate({ id, kind })}>
        {t.vpn.installAutomatic}
      </Button>
      <Button block variant="secondary" icon="terminal" onClick={() => setManual((shown) => !shown)}>
        {manual ? t.vpn.hideManual : t.vpn.installManually}
      </Button>
      {install.isError ? (
        <div className="ul-error" role="alert">
          {errorText(install.error)}
        </div>
      ) : null}
      {manual ? <InstallByCommand id={id} /> : null}
    </>
  );
}

/** При ошибке сначала пробуем снова по SSH; ручная одноразовая команда остаётся запасным вариантом. */
function FailedInstallActions({
  id,
  kind,
  onQueued,
}: {
  id: string;
  kind: VpnKind;
  onQueued: () => Promise<unknown>;
}) {
  const trpc = useTRPC();
  const { t } = usePrefs();
  const [manual, setManual] = useState(false);
  const retry = useMutation(
    trpc.servers.vpnRetry.mutationOptions({
      onSuccess: async () => {
        haptic.success();
        await onQueued();
      },
      onError: () => haptic.error(),
    }),
  );
  return (
    <>
      <Button block icon="shield" loading={retry.isPending} onClick={() => retry.mutate({ id, kind })}>
        {t.vpn.retryAutomatic}
      </Button>
      <Button block variant="secondary" icon="terminal" onClick={() => setManual((shown) => !shown)}>
        {manual ? t.vpn.hideManual : t.vpn.installManually}
      </Button>
      {retry.isError ? (
        <div className="ul-error" role="alert">
          {errorText(retry.error)}
        </div>
      ) : null}
      {manual ? <InstallByCommand id={id} retry /> : null}
    </>
  );
}

/**
 * VPN on the server screen: install progress, then the key as a QR code and text, the apps to connect
 * with and the panel for more keys. A server without one can get it with a command.
 */
export function VpnSection({
  id,
  active,
  reinstall,
}: {
  id: string;
  active: boolean;
  reinstall?: { imageName: string; loading: boolean; onConfirm: () => void };
}) {
  const trpc = useTRPC();
  const { t } = usePrefs();
  const [showPassword, setShowPassword] = useState(false);
  const [another, setAnother] = useState(false);
  const [guideApp, setGuideApp] = useState<VpnApp | null>(null);
  const [clientPlatform, setClientPlatform] = useState<ClientPlatform>(guessClientPlatform);
  const q = useQuery({
    ...trpc.servers.vpn.queryOptions({ id }),
    refetchInterval: (query) => {
      const s = query.state.data?.vpn?.status;
      return s === 'pending' || s === 'installing' ? 5_000 : false;
    },
  });
  const data = q.data;
  if (!data || !active) return null;
  const vpn = data.vpn;
  const name = vpn ? VPN_OPTIONS.find((o) => o.kind === vpn.kind)?.name : null;

  const header = (
    <h2 className="ul-group__header" id="srv-vpn">
      {name ? `${t.vpn.title} · ${name}` : t.vpn.title}
    </h2>
  );

  if (!vpn || another) {
    if (!data.supported) {
      return (
        <section className="ul-stack" aria-labelledby="srv-vpn">
          {header}
          <div className="ul-error" role="alert">
            {t.vpn.failedUnsupported}
          </div>
          {data.reinstallSupported && reinstall ? (
            <ConfirmButton
              variant="secondary"
              loading={reinstall.loading}
              confirmText={t.vpn.reinstallConfirm}
              onConfirm={reinstall.onConfirm}
            >
              {t.vpn.reinstallForVpn(reinstall.imageName)}
            </ConfirmButton>
          ) : (
            <div className="ul-group__footer">{t.vpn.reinstallUnavailable}</div>
          )}
        </section>
      );
    }
    return (
      <section className="ul-stack" aria-labelledby="srv-vpn">
        {header}
        {!vpn ? (
          <div className="ul-mon-intro">
            <Icon name="shield" size={22} />
            <span>{t.vpn.intro}</span>
          </div>
        ) : null}
        <InstallAutomatically id={id} onQueued={() => q.refetch()} />
      </section>
    );
  }

  if (vpn.status === 'pending' || vpn.status === 'installing') {
    const at = vpn.status === 'pending' ? 0 : (PROGRESS[vpn.step ?? ''] ?? 1);
    return (
      <section className="ul-stack" aria-labelledby="srv-vpn">
        {header}
        <div className="ul-group">
          <ol className="ul-tl" aria-live="polite">
            {t.vpn.steps.map((s, i) => (
              <li key={s} className={`ul-tl__step${i < at ? ' is-done' : ''}${i === at ? ' is-active' : ''}`}>
                <span className="ul-tl__dot" aria-hidden>
                  {i < at ? <Icon name="check" size={14} /> : null}
                </span>
                <span className="ul-tl__label">{s}</span>
              </li>
            ))}
          </ol>
          <div className="ul-group__footer" style={{ padding: '0 16px 16px' }}>
            {t.vpn.installingFooter}
          </div>
        </div>
      </section>
    );
  }

  if (vpn.status === 'failed' || !vpn.result) {
    return (
      <section className="ul-stack" aria-labelledby="srv-vpn">
        {header}
        <div className="ul-error" role="alert">
          {!data.supported
            ? t.vpn.failedUnsupported
            : vpn.error === 'timeout'
              ? t.vpn.failedTimeout
              : t.vpn.failed(vpn.error ?? '')}
        </div>
        {data.supported ? (
          <FailedInstallActions id={id} kind={vpn.kind as VpnKind} onQueued={() => q.refetch()} />
        ) : null}
      </section>
    );
  }

  const r = vpn.result;
  const kind = vpn.kind as VpnKind;
  if (guideApp?.id && kind === 'vless') {
    return (
      <section className="ul-stack" aria-labelledby="srv-vpn-app-guide">
        <VpnAppGuide app={guideApp} vpnKey={r.key} onBack={() => setGuideApp(null)} />
      </section>
    );
  }
  return (
    <section className="ul-stack" aria-labelledby="srv-vpn">
      {header}
      <div className="ul-vpn-key">
        <QrCode text={r.key} label={t.vpn.qr} />
        <div className="ul-vpn-key__body">
          <span className="ul-vpn-key__title">
            <Badge tone="ok">{t.vpn.ready}</Badge>
          </span>
          <span className="ul-vpn-key__text">{r.keyType === 'link' ? t.vpn.scanLink : t.vpn.scanConfig}</span>
          <CopyText text={r.key} />
        </div>
      </div>
      {r.keyType === 'link' ? (
        <ListGroup>
          <ListRow
            icon="key"
            title={<span className="ul-mono ul-wrap ul-clamp">{r.key}</span>}
            trailing={<CopyText text={r.key} />}
          />
        </ListGroup>
      ) : null}

      <VpnAppsList
        kind={kind}
        platform={clientPlatform}
        onPlatformChange={setClientPlatform}
        onSelect={setGuideApp}
      />

      {r.panel ? (
        <ListGroup
          header={t.vpn.panel}
          footer={`${t.vpn.panelFooter[kind]}${r.panel.certificateTrusted === false ? ` ${t.vpn.panelCertificateWarning}` : ''}`}
        >
          <ListRow
            icon="globe"
            title={
              <a
                className="ul-panel-url ul-mono ul-wrap"
                href={r.panel.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(event) => {
                  event.preventDefault();
                  openPayUrl(r.panel!.url);
                }}
              >
                {r.panel.url}
              </a>
            }
            trailing={<CopyText text={r.panel.url} label="" />}
          />
          {r.panel.username ? (
            <ListRow
              icon="user"
              title={t.vpn.login}
              value={<span className="ul-mono">{r.panel.username}</span>}
              trailing={<CopyText text={r.panel.username} label="" />}
            />
          ) : null}
          <ListRow
            icon="key"
            title={t.vpn.password}
            value={<span className="ul-mono">{showPassword ? r.panel.password : '••••••••••'}</span>}
            trailing={
              <span className="ul-row-icons">
                <button
                  type="button"
                  className="ul-copy"
                  aria-label={showPassword ? t.common.hide : t.common.show}
                  onClick={() => setShowPassword((v) => !v)}
                >
                  <Icon name={showPassword ? 'eyeOff' : 'eye'} size={16} />
                </button>
                <CopyText text={r.panel.password} label="" />
              </span>
            }
          />
        </ListGroup>
      ) : kind === 'vless' ? (
        <ListGroup header={t.vpn.panel} footer={t.vpn.panelMissingHint}>
          <ListRow icon="alert" tone="crit" title={t.vpn.panelMissing} />
        </ListGroup>
      ) : null}

      {r.manager ? (
        <ListGroup header={t.vpn.manager} footer={t.vpn.managerFooter}>
          <ListRow
            icon="settings"
            title={<span className="ul-mono ul-wrap ul-clamp">{r.manager}</span>}
            trailing={<CopyText text={r.manager} label="" />}
          />
        </ListGroup>
      ) : null}

      <button type="button" className="ul-link ul-link--muted" onClick={() => setAnother(true)}>
        {t.vpn.another}
      </button>
    </section>
  );
}
